import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { randomId } from "@/lib/utils";
import { ApproverNoteBadge, type UnresolvedAnnotation } from "@/components/ApproverNoteBadge";

export type BonusType = "signing" | "relocation" | "retention" | "performance" | "academic" | "media_apparel" | "other";
export type PaymentTiming = "one_time_signing" | "on_trigger" | "annual" | "custom_date";

export type ContractBonus = {
  id: string;
  type: BonusType;
  customLabel?: string;
  label: string;
  amount: number;
  condition?: string;
  paymentTiming: PaymentTiming;
  paymentDate?: string;
  isRecurring: boolean;
  recurringFrequency?: string;
};

export type AllowancePaymentTiming = "monthly" | "annual" | "one_time" | "custom";

export type ContractAllowance = {
  id: string;
  type: string;
  label: string;
  amount: number;
  paymentTiming: AllowancePaymentTiming;
  notes?: string;
};

const BONUS_TYPE_LABELS: Record<BonusType, string> = {
  signing: "Signing",
  relocation: "Relocation",
  retention: "Retention",
  performance: "Performance",
  academic: "Academic",
  media_apparel: "Media / Apparel",
  other: "Other",
};

const PAYMENT_TIMING_LABELS: Record<PaymentTiming, string> = {
  one_time_signing: "One-time at signing",
  on_trigger: "On trigger / condition met",
  annual: "Annual",
  custom_date: "Custom date",
};

const ALLOWANCE_PAYMENT_LABELS: Record<AllowancePaymentTiming, string> = {
  monthly: "Monthly",
  annual: "Annual",
  one_time: "One-time",
  custom: "Custom",
};

const COMMON_ALLOWANCE_TYPES = [
  "Vehicle",
  "Housing",
  "Country Club",
  "Courtesy Car",
  "Phone",
  "Entertainment",
  "Moving / Relocation",
  "Other",
];

function emptyBonus(): ContractBonus {
  return {
    id: randomId(),
    type: "performance",
    label: "",
    amount: 0,
    condition: "",
    paymentTiming: "on_trigger",
    paymentDate: "",
    isRecurring: false,
    recurringFrequency: "",
  };
}

function emptyAllowance(): ContractAllowance {
  return {
    id: randomId(),
    type: "Vehicle",
    label: "",
    amount: 0,
    paymentTiming: "monthly",
    notes: "",
  };
}

function moveItem<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
  if (toIndex < 0 || toIndex >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}

interface BonusRepeaterProps {
  bonuses: ContractBonus[];
  onChange: (bonuses: ContractBonus[]) => void;
  /**
   * Approver notes keyed by stable field name. Aggregate notes live under
   * `bonuses` while per-row notes use `bonuses:<id>` so the badge can render
   * next to the matching repeater row.
   */
  notesByField?: Record<string, UnresolvedAnnotation[]>;
  approvalId?: number | null;
  onNoteResolved?: () => void;
}

export function BonusRepeater({ bonuses, onChange, notesByField, approvalId, onNoteResolved }: BonusRepeaterProps) {
  const update = (id: string, patch: Partial<ContractBonus>) => {
    onChange(bonuses.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const remove = (id: string) => {
    onChange(bonuses.filter((b) => b.id !== id));
  };

  const add = () => {
    onChange([...bonuses, emptyBonus()]);
  };

  const move = (idx: number, direction: "up" | "down") => {
    onChange(moveItem(bonuses, idx, direction === "up" ? idx - 1 : idx + 1));
  };

  const totalFixed = bonuses.filter((b) => !b.isRecurring && b.paymentTiming !== "on_trigger").reduce((s, b) => s + (b.amount || 0), 0);
  const totalConditional = bonuses.filter((b) => b.paymentTiming === "on_trigger" && !b.isRecurring).reduce((s, b) => s + (b.amount || 0), 0);
  const totalRecurring = bonuses.filter((b) => b.isRecurring).reduce((s, b) => s + (b.amount || 0), 0);
  const grandTotal = bonuses.reduce((s, b) => s + (b.amount || 0), 0);

  return (
    <div className="space-y-4">
      {bonuses.length === 0 && (
        <div className="text-sm text-muted-foreground italic py-2">No bonuses added yet. Click "Add bonus" to get started.</div>
      )}

      {bonuses.map((bonus, idx) => (
        <Card key={bonus.id} className="border border-border">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  disabled={idx === 0}
                  onClick={() => move(idx, "up")}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  disabled={idx === bonuses.length - 1}
                  onClick={() => move(idx, "down")}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium text-muted-foreground ml-1">Bonus {idx + 1}</span>
                <ApproverNoteBadge
                  notes={notesByField?.[`bonuses:${bonus.id}`]}
                  field={`bonuses-row-${bonus.id}`}
                  approvalId={approvalId}
                  onResolved={onNoteResolved}
                />
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(bonus.id)} className="h-7 w-7 text-destructive hover:bg-destructive/10">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Type *</Label>
                <Select value={bonus.type} onValueChange={(v) => update(bonus.id, { type: v as BonusType })}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(BONUS_TYPE_LABELS) as BonusType[]).map((t) => (
                      <SelectItem key={t} value={t}>{BONUS_TYPE_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {bonus.type === "other" ? (
                <div className="space-y-1">
                  <Label className="text-xs">Custom Label *</Label>
                  <Input
                    className="h-8 text-sm"
                    placeholder="e.g. Apparel Revenue Share"
                    value={bonus.customLabel ?? ""}
                    onChange={(e) => update(bonus.id, { customLabel: e.target.value, label: e.target.value })}
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  <Label className="text-xs">Label / Description *</Label>
                  <Input
                    className="h-8 text-sm"
                    placeholder="e.g. Win SEC Championship"
                    value={bonus.label}
                    onChange={(e) => update(bonus.id, { label: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Amount ($) *</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1.5 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    className="h-8 text-sm pl-6"
                    placeholder="0"
                    value={bonus.amount || ""}
                    onChange={(e) => update(bonus.id, { amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Payment Timing *</Label>
                <Select value={bonus.paymentTiming} onValueChange={(v) => update(bonus.id, { paymentTiming: v as PaymentTiming })}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PAYMENT_TIMING_LABELS) as PaymentTiming[]).map((t) => (
                      <SelectItem key={t} value={t}>{PAYMENT_TIMING_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {bonus.paymentTiming === "custom_date" && (
              <div className="space-y-1">
                <Label className="text-xs">Payment Date</Label>
                <Input
                  type="date"
                  className="h-8 text-sm"
                  value={bonus.paymentDate ?? ""}
                  onChange={(e) => update(bonus.id, { paymentDate: e.target.value })}
                />
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs">Trigger / Condition</Label>
              <Textarea
                className="text-sm min-h-[60px] resize-none"
                placeholder="e.g. Win conference championship, Graduation rate ≥ 80%, On roster as of Jan 1, 2027"
                value={bonus.condition ?? ""}
                onChange={(e) => update(bonus.id, { condition: e.target.value })}
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id={`recurring-${bonus.id}`}
                checked={bonus.isRecurring}
                onCheckedChange={(checked) => update(bonus.id, { isRecurring: !!checked })}
              />
              <Label htmlFor={`recurring-${bonus.id}`} className="text-sm cursor-pointer">Recurring</Label>
            </div>

            {bonus.isRecurring && (
              <div className="space-y-1">
                <Label className="text-xs">Recurring Frequency</Label>
                <Input
                  className="h-8 text-sm"
                  placeholder="e.g. Annually, Every 2 years"
                  value={bonus.recurringFrequency ?? ""}
                  onChange={(e) => update(bonus.id, { recurringFrequency: e.target.value })}
                />
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-2">
        <Plus className="h-4 w-4" />
        Add bonus
      </Button>

      {bonuses.length > 0 && (
        <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 space-y-1 text-sm">
          <div className="font-medium">Total Bonuses Summary</div>
          {totalFixed > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Fixed / guaranteed</span>
              <span className="font-medium text-foreground">${totalFixed.toLocaleString()}</span>
            </div>
          )}
          {totalConditional > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Conditional (on trigger)</span>
              <span className="font-medium text-foreground">${totalConditional.toLocaleString()} <span className="text-xs text-muted-foreground">(if earned)</span></span>
            </div>
          )}
          {totalRecurring > 0 && (
            <div className="flex justify-between text-muted-foreground">
              <span>Recurring (per period)</span>
              <span className="font-medium text-foreground">${totalRecurring.toLocaleString()} <span className="text-xs text-muted-foreground">(recurring)</span></span>
            </div>
          )}
          <div className="flex justify-between border-t border-border pt-1 mt-1">
            <span className="font-semibold">Total (all rows)</span>
            <span className="font-bold">${grandTotal.toLocaleString()}</span>
          </div>
          {(totalConditional > 0 || totalRecurring > 0) && (
            <p className="text-xs text-muted-foreground mt-1">* Conditional and recurring amounts are not guaranteed — total includes all rows as if fully earned.</p>
          )}
        </div>
      )}
    </div>
  );
}

interface AllowanceRepeaterProps {
  allowances: ContractAllowance[];
  onChange: (allowances: ContractAllowance[]) => void;
  /**
   * Approver notes keyed by stable field name. Aggregate notes live under
   * `allowances` while per-row notes use `allowances:<id>` so the badge can
   * render next to the matching repeater row.
   */
  notesByField?: Record<string, UnresolvedAnnotation[]>;
  approvalId?: number | null;
  onNoteResolved?: () => void;
}

export function AllowanceRepeater({ allowances, onChange, notesByField, approvalId, onNoteResolved }: AllowanceRepeaterProps) {
  const update = (id: string, patch: Partial<ContractAllowance>) => {
    onChange(allowances.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const remove = (id: string) => {
    onChange(allowances.filter((a) => a.id !== id));
  };

  const add = () => {
    onChange([...allowances, emptyAllowance()]);
  };

  const move = (idx: number, direction: "up" | "down") => {
    onChange(moveItem(allowances, idx, direction === "up" ? idx - 1 : idx + 1));
  };

  const total = allowances.reduce((s, a) => s + (a.amount || 0), 0);

  return (
    <div className="space-y-4">
      {allowances.length === 0 && (
        <div className="text-sm text-muted-foreground italic py-2">No allowances added. Click "Add allowance" to specify vehicle, housing, or other perks.</div>
      )}

      {allowances.map((allowance, idx) => (
        <Card key={allowance.id} className="border border-border">
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  disabled={idx === 0}
                  onClick={() => move(idx, "up")}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground"
                  disabled={idx === allowances.length - 1}
                  onClick={() => move(idx, "down")}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium text-muted-foreground ml-1">Allowance {idx + 1}</span>
                <ApproverNoteBadge
                  notes={notesByField?.[`allowances:${allowance.id}`]}
                  field={`allowances-row-${allowance.id}`}
                  approvalId={approvalId}
                  onResolved={onNoteResolved}
                />
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(allowance.id)} className="h-7 w-7 text-destructive hover:bg-destructive/10">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Category *</Label>
                <Select value={allowance.type} onValueChange={(v) => update(allowance.id, { type: v })}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_ALLOWANCE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Label / Description *</Label>
                <Input
                  className="h-8 text-sm"
                  placeholder="e.g. Monthly car allowance"
                  value={allowance.label}
                  onChange={(e) => update(allowance.id, { label: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Amount ($) *</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1.5 text-muted-foreground text-sm">$</span>
                  <Input
                    type="number"
                    className="h-8 text-sm pl-6"
                    placeholder="0"
                    value={allowance.amount || ""}
                    onChange={(e) => update(allowance.id, { amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Payment Frequency *</Label>
                <Select value={allowance.paymentTiming} onValueChange={(v) => update(allowance.id, { paymentTiming: v as AllowancePaymentTiming })}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(Object.keys(ALLOWANCE_PAYMENT_LABELS) as AllowancePaymentTiming[]).map((t) => (
                      <SelectItem key={t} value={t}>{ALLOWANCE_PAYMENT_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Input
                className="h-8 text-sm"
                placeholder="e.g. Taxable benefit, requires receipts"
                value={allowance.notes ?? ""}
                onChange={(e) => update(allowance.id, { notes: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={add} className="gap-2">
        <Plus className="h-4 w-4" />
        Add allowance
      </Button>

      {allowances.length > 0 && (
        <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 space-y-1 text-sm">
          <div className="font-medium">Total Allowances</div>
          <div className="flex justify-between">
            <span className="font-semibold">Total (all rows)</span>
            <span className="font-bold">${total.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}
